import { ROUTER_PATH } from "@config/router";

const readString = (value: unknown): string | undefined =>
  typeof value === "string" ? value : undefined;

const isCryptoAssetType = (attrs: Record<string, unknown>): boolean => {
  const raw = attrs.assetType ?? attrs.asset_type;
  const s = readString(raw)?.toLowerCase();
  return s === "crypto";
};

/**
 * Builds href for notification `view_detail`: `/base/{symbol}[/{tab}]` — `tab` is passed through
 * from the backend. Each dynamic path segment is encoded separately (never encode the full path:
 * that would turn `/` into `%2F` and break routing).
 *
 * **Crypto needs `assetRouteId`, not `symbol`** (2026-04-28 `fix: fix crypto router path`):
 * crypto detail pages are keyed by route id. When it is missing we return `null` so the caller
 * skips navigation — previously this built `/crypto/` and navigated to a dead URL, which the
 * user could reach by tapping "View details" on a notification card.
 */
export function buildViewDetailHref(
  attrs: Record<string, unknown>,
): string | null {
  const legacyLink = readString(attrs.link);
  const symbol = readString(attrs.symbol);
  const tab = readString(attrs.tab);
  const cryptoId = readString(attrs.assetRouteId);

  if (symbol) {
    const encSymbol = encodeURIComponent(symbol);
    const normalizedTab = tab?.toLowerCase() === "score" ? "edgen-ai" : tab;
    const encCryptoId = cryptoId ? encodeURIComponent(cryptoId) : "";
    const encTab = normalizedTab ? encodeURIComponent(normalizedTab) : "";
    const tail = encTab ? `/${encTab}` : "";
    if (isCryptoAssetType(attrs)) {
      // 没有 route id 就没有可去的页面 —— 返回 null，调用方 `if (href)` 会跳过导航
      return encCryptoId
        ? `${ROUTER_PATH.CRYPTO_TOKEN}/${encCryptoId}${tail}`
        : null;
    }
    return `${ROUTER_PATH.STOCK_DETAIL}/${encSymbol}${tail}`;
  }

  if (legacyLink) {
    return `${ROUTER_PATH.STOCK_DETAIL}/${encodeURIComponent(legacyLink)}`;
  }
  return null;
}
